import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { Datagrid, BooleanInput, DateInput } from 'react-admin';
import List from 'components/List';
import PositionInput from 'components/PositionInput';
import LinkField from 'components/LinkField';
import BulkUploadButtons from 'components/BulkUploadButtons';
import MoneyField from 'components/MoneyField';
import DateTimeField from 'components/DateTimeField';
import UsingBlendedRateField from 'components/UsingBlendedRateField';
import { getSettingsQuery } from '../utils/getSettingsQuery';
import useGate from '../../../hooks/useGate';
import { gateNames } from '../../../constants/statsig';
import { SettingsNestedOperatorSelect } from 'components/SettingsNestedOperatorSelect';
import { IdFilter } from 'components/Filter';

const LocationPositionRateSettingListFilters = [
  <IdFilter />,
  <PositionInput emptyText="All" alwaysOn />,
  <IdFilter label="Location ID" source="location_id" alwaysOn />,
  <BooleanInput
    label="Below Market LT/Market/State LT/State Rate"
    alwaysOn
    source="search.below_market_rate"
  />,
  <DateInput alwaysOn label="Updated after" source="search.updated_at_from" />,
];

const bulkUploadButtons = (
  <BulkUploadButtons
    downloadUrl="https://medely-public-files.s3.us-west-2.amazonaws.com/csv_templates/location_position_rate_settings_bulk_upload_template.csv"
    uploadUrl="v3/admin/location_position_rate_settings/import"
  />
);

const LocationPositionRateSettingList = () => {
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);
  const [settingsNestedOperator, setSettingsNestedOperator] = useState('ten99');

  return (
    <List
      filters={LocationPositionRateSettingListFilters}
      exporter={false}
      additionalActions={blockCreateRateSettings ? null : bulkUploadButtons}
      filter={{ active: true }}
    >
      <SettingsNestedOperatorSelect
        setSettingsNestedOperator={setSettingsNestedOperator}
        settingsNestedOperator={settingsNestedOperator}
      />
      <Datagrid bulkActionButtons={false}>
        <LinkField label="ID" source="id" />
        <LinkField label="Location ID" source="location_id" />
        <LinkField label="Position" source="position.display_name" sortBy="position_name" />
        <LinkField label="Specialty" source="specialty.label" sortBy="specialty_name" />
        <MoneyField
          label="Charge Rate"
          source={`settings.${settingsNestedOperator}.charge_rate_cents`}
          blankIfNull={true}
          sortBy={`${settingsNestedOperator.replace('.', '_')}_charge_rate_cents`}
        />

        <UsingBlendedRateField forceNestedObject={settingsNestedOperator} />

        <DateTimeField
          format="MM/dd/yyyy HH:mm"
          source="updated_at"
          timeZone="America/Los_Angeles"
        />
      </Datagrid>
    </List>
  );
};

LocationPositionRateSettingList.query = gql`
  query locationPositionRateSettings($input: LocationPositionRateSettingQueryInput) {
    _locationPositionRateSettingsMeta(input: $input) {
      count
    }
    locationPositionRateSettings(input: $input) {
      id
      active
      location_id
      location_position_rate_setting_active
      location_position_rate_setting_id
      market_position_rate_setting_active
      market_position_rate_setting_id
      position {
        id
        display_name
      }
      location {
        primary_billing_group {
          plan
        }
      }
      position_id
      ${getSettingsQuery()}
      specialty {
        id
        label
      }
      specialty_id
      state_position_rate_setting_active
      state_position_rate_setting_id
      updated_at
    }
  }
`;

export default LocationPositionRateSettingList;
